import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * Discriminated union for editor commands.
 * All components (variable tree, table builder, IF dialog, artefact panel, copy popover)
 * emit commands via this service. The editor component subscribes and delegates to
 * TinyMCE or CodeMirror depending on the active editor mode.
 */
export type TeEditorCommand =
  | { type: 'insertVariable'; path: string }
  | { type: 'insertArtefact'; id: number; name: string }
  | { type: 'insertFreeMarker'; code: string }
  | { type: 'insertTable'; html: string }
  | { type: 'openTableBuilder'; entityAlias: string; entityId: number; collectionPath: string; itemAlias: string; collectionLabel: string }
  | { type: 'openIfDialog'; varPath: string; label: string; dataType: string; enumValues: string[] }
  | { type: 'replaceAll'; search: string; replace: string };

export type TeEditorMode = 'wysiwyg' | 'code';

/**
 * Communication service for the TE editor ecosystem.
 *
 * Replaces the old drag-and-drop based communication between components with
 * a clean, typed command pattern using RxJS subjects.
 *
 * Usage:
 * - Components call insertVariable(), insertArtefact(), etc.
 * - TeEditorComponent subscribes to command$ and executes accordingly.
 */
@Injectable()
export class TeEditorCommunicationService {

  /** Stream of editor commands to be executed. */
  private commandSubject = new Subject<TeEditorCommand>();
  command$: Observable<TeEditorCommand> = this.commandSubject.asObservable();

  /** Current active editor mode. */
  private editorModeSubject = new BehaviorSubject<TeEditorMode>('wysiwyg');
  editorMode$: Observable<TeEditorMode> = this.editorModeSubject.asObservable();

  /** Dirty state tracking. */
  private dirtySubject = new BehaviorSubject<boolean>(false);
  dirty$: Observable<boolean> = this.dirtySubject.asObservable();

  /**
   * Insert a variable expression at the current cursor position.
   * Generates ${path} in code mode, or a variable chip in WYSIWYG mode.
   * @param path The FreeMarker variable path (e.g., "customer.firstName")
   */
  insertVariable(path: string): void {
    this.commandSubject.next({ type: 'insertVariable', path });
  }

  /**
   * Insert an artefact reference at the current cursor position.
   * Generates #artefact:N# in code mode, or an artefact chip in WYSIWYG mode.
   * @param id The artefact ID
   * @param name The artefact display name
   */
  insertArtefact(id: number, name: string): void {
    this.commandSubject.next({ type: 'insertArtefact', id, name });
  }

  /**
   * Insert arbitrary FreeMarker code at the current cursor position.
   * Used for IF/ELSE blocks, loops, and other FreeMarker constructs.
   * @param code The raw FreeMarker code to insert
   */
  insertFreeMarker(code: string): void {
    this.commandSubject.next({ type: 'insertFreeMarker', code });
  }

  /**
   * Insert a table HTML fragment at the current cursor position.
   * Used by the Table Builder dialog.
   * @param html The generated HTML table with FreeMarker expressions
   */
  insertTable(html: string): void {
    this.commandSubject.next({ type: 'insertTable', html });
  }

  /**
   * Open the IF/ELSE dialog for a given variable with context-aware options.
   * @param varPath The FreeMarker variable path
   * @param label Human-readable label for the variable
   * @param dataType Simple data type ('string', 'boolean', 'enum', 'date', 'currency', 'number')
   * @param enumValues Enum value labels (only for dataType === 'enum')
   */
  openIfDialog(varPath: string, label: string, dataType: string, enumValues: string[]): void {
    this.commandSubject.next({ type: 'openIfDialog', varPath, label, dataType, enumValues });
  }

  /**
   * Open the table builder dialog for a given collection.
   * Used when the user clicks TABLE on a collection variable node.
   * @param entityAlias The entity alias for the collection item type (e.g., "cm.Contract")
   * @param collectionPath The full FreeMarker path to the collection (e.g., "customer.customerContracts")
   * @param itemAlias The loop variable alias (e.g., "contract")
   * @param collectionLabel Human-readable label for the collection
   */
  openTableBuilder(entityAlias: string, entityId: number, collectionPath: string, itemAlias: string, collectionLabel: string): void {
    this.commandSubject.next({ type: 'openTableBuilder', entityAlias, entityId, collectionPath, itemAlias, collectionLabel });
  }

  /**
   * Replace all occurrences of a search string with a replacement string
   * in the current editor content. Used for variable renames.
   * @param search The string to search for
   * @param replace The replacement string
   */
  replaceAll(search: string, replace: string): void {
    this.commandSubject.next({ type: 'replaceAll', search, replace });
  }

  /**
   * Get the current active editor mode.
   * @returns 'wysiwyg' or 'code'
   */
  getActiveEditorMode(): TeEditorMode {
    return this.editorModeSubject.getValue();
  }

  /**
   * Set the active editor mode. Called by the editor component when the mode changes.
   * @param mode The new editor mode
   */
  setActiveEditorMode(mode: TeEditorMode): void {
    this.editorModeSubject.next(mode);
  }

  /**
   * Mark the editor content as dirty (unsaved changes).
   */
  markDirty(): void {
    this.dirtySubject.next(true);
  }

  /**
   * Mark the editor content as clean (saved).
   */
  markClean(): void {
    this.dirtySubject.next(false);
  }

  /**
   * Check if the editor has unsaved changes.
   */
  isDirty(): boolean {
    return this.dirtySubject.getValue();
  }
}
